import { FC, useEffect, useState } from "react";
import SubmitItem from "../../../components/template/SubmitItem/SubmitItem";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation, Form } from "react-router-dom";
import { Toast } from "../../../components/atom/Toast/Toast";
import { OrganizationItemSchema } from "../data/OrganizationItemSchema";
import {
  EDIT_ORGANIZATION_URL,
  ORGANIZATION_URL,
} from "../../../utils/endpoints";
import { handleSubmitItem } from "../../../helpers/handleSubmitItem";
import { handleDeleteItem } from "../../../helpers/handleDeleteItem";
import OrganizationSubmitForm from "./OrganizationSubmitForm";
import { useAuth } from "../../../provider/AuthProvider";
import { AlertDialogConfig } from "../../../types/Components/Alert/AlertDialogConfig";
import api from "../../../utils/api";

const EditOrganization: FC = () => {
  const { addToast } = Toast();
  const navigate = useNavigate();
  const location = useLocation();
  const { organization } = location.state || {};
  const [idItem] = useState<number>(organization?.id || 0);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState<string>(organization?.name || "");
  const [isLoaded, setIsLoaded] = useState(true);

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    resetField,
    setValue,
  } = useForm({
    resolver: zodResolver(OrganizationItemSchema),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  const { auth } = useAuth();
  const user = auth.user;

  useEffect(() => {
    if (organization) {
      setValue("name", organization.name);
      setValue("email", organization.email ? organization.email : "");
      setValue("organizationLevelOption", organization.organizationLevel);
      setValue(
        "provinceOption",
        organization.province ? organization.province : undefined,
      );
      setValue(
        "countyOption",
        organization.county ? organization.county : undefined,
      );
      setIsFormInitialized(true);

      setTitle(organization.name);
    }
  }, [organization, setValue]);

  const onSubmit = async (data: FieldValues) => {
    const bodyRequest = {
      id: idItem,
      name: data.name,
      email: data.email || null,
      province: data.provinceOption?.id || null,
      county: data.countyOption?.id || null,
      organizationLevel: data.organizationLevelOption.id,
    };

    await handleSubmitItem(
      () => api.patch(EDIT_ORGANIZATION_URL, bodyRequest),
      addToast,
      setIsLoaded,
    );
    navigate("/entidades");
  };

  const handleDelete = () => {
    handleDeleteItem(
      ORGANIZATION_URL,
      setIsAlertOpen,
      idItem,
      addToast,
      setIsLoaded,
    );
  };

  const alertDialogConfig: AlertDialogConfig = {
    setIsAlertOpen,
    isAlertOpen,
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <SubmitItem
        title={title}
        formTitle="Dados institucionais"
        handleSubmitItem={handleSubmit(onSubmit)}
        handleDeleteItem={
          user.permissions.includes("DeleteOrganizations")
            ? handleDelete
            : undefined
        }
        deleteButtonName="Eliminar entidade"
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        navigatePath="/entidades"
        alertDialogConfig={alertDialogConfig}
        isLoaded={isLoaded}
      >
        <OrganizationSubmitForm
          errors={errors}
          control={control}
          resetField={resetField}
          watch={watch}
          isFormInitialized={isFormInitialized}
          isDisabled={!isEditing}
        />
      </SubmitItem>
    </Form>
  );
};

export default EditOrganization;
